import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { SpacesRemainingTag } from 'Modules/FerrySchedule/Components/SpacesRemaining/SpacesRemainingTag';
import React from 'react';
import { useIntl } from 'react-intl';

import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { LegOptionContext } from './Contexts/LegOptionContext';
import { isWalkOnNotOffered, walkOnNotOfferedMessage } from 'Modules/MultiStopWizard/Hooks/walkOnNotOffered';

export default function LegOptionPaxCapacity() {
	const context = React.useContext(LegOptionContext);
	const intl = useIntl();

	if (!context) {
		console.error('LegOptionContext is missing in LegOptionPaxCapacity');
		return null;
	}

	const { paxSpaces } = context;

	// If the user is creating a "vehicle" booking and the application is configured to not allow passengers,
	// then we should not show the pax capacity option.
	// OR if the user is creating a "freight" booking, then we should not show the pax capacity option.
	if (
		(context.wizardData.tabSelected === 'vehicle' && !appConfigStore.vehicleHasPassengersEnabled) ||
		context.wizardData.tabSelected === 'freight'
	) {
		return null;
	}

	// On the walk-on tab, a vessel that doesn't offer walk-ons reads as closed: red dot rather than
	// green, so the chip agrees with the greyed-out row instead of advertising seats nobody can book.
	// Walk-on tab only — on the vehicle tab this capacity is real, because passengers still travel on
	// this vessel inside vehicles, and a red dot there would say otherwise.
	// Its own reason, not "closed": see `isWalkOnNotOffered`. The sailing being closed to passengers
	// as well is closed, and says so in the usual way.
	const walkOnsNotOffered = isWalkOnNotOffered(context.trip, context.wizardData.tabSelected);

	return (
		<LegOptionPaxCapacityInner
			space={paxSpaces}
			closed={context.passengerBookingsClosed || walkOnsNotOffered}
			closedReason={walkOnsNotOffered ? intl.formatMessage(walkOnNotOfferedMessage) : undefined}
			showName={context.showCapacityNames}
			explainClosed={context.explainClosedCapacity}
			explainState={context.explainCapacityState}
		/>
	);
}

interface LegOptionPaxCapacityInnerProps {
	space?: number;
	capacityName?: string;
	closed: boolean;
	/** Names the capacity beside its icon. See `showCapacityNames` on the leg-option context. */
	showName?: boolean;
	/** Explains `(closed)`. See `explainClosedCapacity` on the leg-option context. */
	explainClosed?: boolean;
	/** Tooltip describes the state, not the name. See `explainCapacityState` on the context. */
	explainState?: boolean;
	/** Overrides the default tooltip, e.g. to say why the indicator is red. */
	tooltipText?: string;
	/** Why it is closed, where that is not the usual reason. See `closedReason` on `SpacesRemainingTag`. */
	closedReason?: string;
}

export function LegOptionPaxCapacityInner({
	space,
	capacityName,
	closed,
	showName = false,
	explainClosed = false,
	explainState = false,
	tooltipText,
	closedReason,
}: LegOptionPaxCapacityInnerProps) {
	return (
		<SpacesRemainingTag
			space={space ?? 0}
			icon={<Icon name="person" useMaterialIcons />}
			// Customers only see the passenger capacity numbers when the app config allows it;
			// otherwise just a green/red indicator. Staff always see the numbers.
			showCapacityRemaining={store.isStaff || appConfigStore.passengerCapacityVisibleToCustomers}
			closed={closed}
			showName={showName}
			explainClosed={explainClosed}
			explainState={explainState}
			capacityName={capacityName ?? 'Passengers'}
			tooltipText={showName ? tooltipText : tooltipText ?? capacityName ?? 'Passengers'}
			closedReason={closedReason}
		/>
	);
}
